import React from 'react';
import { SegmentedTabs, TextField } from '@/components/common';
import { ContactChannel, formatMobile } from '@/utils/loginContact';

interface ContactFieldProps {
  channel: ContactChannel;
  onChannelChange: (channel: ContactChannel) => void;
  mobile: string;
  /** Digits only, at most 10. */
  onMobileChange: (digits: string) => void;
  email: string;
  onEmailChange: (email: string) => void;
  /** Field labels, e.g. "Mobile number used at checkout". */
  labels: Record<ContactChannel, string>;
  onBlur?: () => void;
  error?: string;
  /** Read-only once a code has been sent; "Change" calls `onChange`. */
  locked?: boolean;
  onChange?: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
}

const CHANNELS = [
  { value: 'mobile' as const, label: 'Mobile' },
  { value: 'email' as const, label: 'Email' },
];

/** "Mobile / Email" switch with the matching field; mobile is the default (Figma L1, F2). */
const ContactField: React.FC<ContactFieldProps> = ({
  channel,
  onChannelChange,
  mobile,
  onMobileChange,
  email,
  onEmailChange,
  labels,
  onBlur,
  error,
  locked,
  onChange,
  inputRef,
}) => {
  const change = locked && onChange && (
    <button
      type="button"
      onClick={onChange}
      className="tw-shrink-0 tw-rounded tw-text-[14px] tw-font-semibold tw-leading-5 tw-text-ds-brand-text hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
    >
      Change
    </button>
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <SegmentedTabs items={CHANNELS} value={channel} onChange={onChannelChange} label="Sign in with" className="tw-w-full [&>button]:tw-flex-1" />
      {channel === 'mobile' ? (
        <TextField
          key="mobile"
          ref={inputRef}
          label={labels.mobile}
          prefix="+91"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder="98765 43210"
          value={formatMobile(mobile)}
          onChange={(e) => onMobileChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
          onBlur={onBlur}
          readOnly={locked}
          trailing={change}
          error={error}
        />
      ) : (
        <TextField
          key="email"
          ref={inputRef}
          label={labels.email}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="you@example.com"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          onBlur={onBlur}
          readOnly={locked}
          trailing={change}
          error={error}
        />
      )}
    </div>
  );
};

export default ContactField;
